import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import usersRequest from "../../request/usersRequest";
import { CombinedContext } from "../../context/combinedContext";
import { clearTokens, setTokens } from "../../auth/tokenManager";
import translations from "../../translations/translations";
import PasswordField from "../common/passwordField/passwordField";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import TermsOfServiceModal from "../common/termsOfServiceModal/termsOfServiceModal";
import logo from "../../images/LogoFullText.png";
import "./registerPage.css";
import PhoneInput from "../common/phoneInput/phoneInput";
import PasswordStrength from "../common/passwordStrength/passwordStrength";
import { Check, X } from "lucide-react";

const RegisterPage = () => {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [rememberMe, setRememberMe] = useState(true);
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const [loading, setLoading] = useState(false);
	const [activeModal, setActiveModal] = useState(null);
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);
	const [phone, setPhone] = useState("");
	const [invalidFields, setInvalidFields] = useState(new Set());

	const { language, setUserData } = useContext(CombinedContext);
	const navigate = useNavigate();

	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);
		setTimeout(() => setExiting(true), duration - 250);
		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	/* The banner says what's wrong; these say where. A flagged field clears
	   as soon as it's touched, so the red only ever marks what's still open. */
	const clearInvalid = (field) => {
		setInvalidFields((prev) => {
			if (!prev.has(field)) return prev;
			const next = new Set(prev);
			next.delete(field);
			return next;
		});
	};

	const invalidClass = (field) => (invalidFields.has(field) ? " is-invalid" : "");

	function isStrongPassword(pwd) {
		return (
			typeof pwd === "string" &&
			pwd.length >= 8 &&
			/[A-Z]/.test(pwd) &&
			/[a-z]/.test(pwd) &&
			/[0-9]/.test(pwd) &&
			/[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`+=-]/.test(pwd)
		);
	}

	const isValidPhone = (p) => {
		const digits = (p || "").replace(/\D/g, "");
		return digits.length >= 11;
	};

	const handleRegister = async () => {
		const missing = new Set();
		if (!firstName) missing.add("firstName");
		if (!lastName) missing.add("lastName");
		if (!email) missing.add("email");
		if (!isValidPhone(phone)) missing.add("phone");
		if (!password) missing.add("password");
		if (!confirmPassword) missing.add("confirmPassword");

		if (missing.size > 0) {
			setInvalidFields(missing);
			showBanner("error", language === "es" ? "¡Casi listo! Completa los campos obligatorios para continuar." : "Almost there! Please fill in the required fields to continue.");
			return;
		}

		if (!acceptedTerms) {
			setInvalidFields(new Set(["terms"]));
			showBanner("error", translations[language].registerShop.errors.mustAcceptTerms);
			return;
		}

		if (!isStrongPassword(password)) {
			setInvalidFields(new Set(["password"]));
			showBanner(
				"error",
				language === "es"
					? "Tu contraseña necesita al menos 8 caracteres, con una mayúscula, una minúscula, un número y un carácter especial."
					: "Your password needs at least 8 characters, including an uppercase letter, a lowercase letter, a number, and a special character.",
			);
			return;
		}

		if (password !== confirmPassword) {
			setInvalidFields(new Set(["confirmPassword"]));
			showBanner("error", language === "es" ? "Las contraseñas no coinciden. Revísalas e inténtalo de nuevo." : "Those passwords don't match — give it another look.");
			return;
		}

		setInvalidFields(new Set());
		setLoading(true);

		try {
			const res = await usersRequest.register({
				email,
				password,
				firstName,
				lastName,
				phone,
				acceptedTermsVersion: "1.0",
			});

			const formattedData = {
				user: res.user,
				appToken: res.appToken,
				cognitoTokens: {
					IdToken: res.cognitoTokens?.IdToken || res.IdToken,
					AccessToken: res.cognitoTokens?.AccessToken || res.AccessToken,
					RefreshToken: res.cognitoTokens?.RefreshToken || res.RefreshToken,
				},
			};

			setUserData(formattedData);

			const storage = rememberMe ? localStorage : sessionStorage;
			storage.setItem("user", JSON.stringify(formattedData.user));
			if (formattedData.appToken) storage.setItem("appToken", formattedData.appToken);
			// Centralised so a leftover token in the other storage cannot
			// shadow this brand-new session.
			clearTokens();
			setTokens(
				{
					idToken: formattedData.cognitoTokens?.IdToken,
					accessToken: formattedData.cognitoTokens?.AccessToken,
					refreshToken: formattedData.cognitoTokens?.RefreshToken,
				},
				{ storage }
			);

			navigate("/");
		} catch (err) {
			console.error("Registration failed:", err);

			const code = err.response?.data?.error;

			const errorMessages = {
				email_in_use: {
					es: "Este correo ya tiene una cuenta. Intenta iniciar sesión en su lugar.",
					en: "This email already has an account. Try signing in instead.",
				},
				invalid_password: {
					es: "Tu contraseña no cumple los requisitos mínimos (mayúscula, minúscula y un número, mínimo 8 caracteres).",
					en: "Your password doesn't meet the minimum requirements (uppercase, lowercase, and a number, at least 8 characters).",
				},
				invalid_parameter: {
					es: "Revisa tu correo y número de teléfono — alguno de los dos no tiene un formato válido.",
					en: "Please check your email and phone number — one of them isn't in a valid format.",
				},
				too_many_requests: {
					es: "Demasiados intentos. Espera un momento e inténtalo de nuevo.",
					en: "Too many attempts. Please wait a moment and try again.",
				},
			};

			const msg = errorMessages[code]?.[language] ?? translations[language].register.error;

			showBanner("error", msg);
		} finally {
			setLoading(false);
		}
	};

	const t = translations[language].register;

	return (
		<div className="sf-auth-page is-wide sf-rg-root">
			<div className="sf-auth-shell">
				<div className="sf-auth-card">
					<img src={logo} alt="Sin Fila" className="sf-auth-logo" />

					<div className="sf-auth-head">
						<h1 className="sf-auth-title">{t.title}</h1>
						<p className="sf-auth-sub">{t.subtitle}</p>
					</div>

					<p className="sf-auth-legend">{t.sectionYou}</p>

					{/* Name row */}
					<div className="sf-auth-row2">
						<div className="sf-auth-field">
							<label className="sf-auth-label">{t.firstName}</label>
							<input
								className={`sf-auth-input${invalidClass("firstName")}`}
								placeholder={t.firstName}
								value={firstName}
								onChange={(e) => {
									setFirstName(e.target.value);
									clearInvalid("firstName");
								}}
								autoComplete="given-name"
							/>
						</div>
						<div className="sf-auth-field">
							<label className="sf-auth-label">{t.lastName}</label>
							<input
								className={`sf-auth-input${invalidClass("lastName")}`}
								placeholder={t.lastName}
								value={lastName}
								onChange={(e) => {
									setLastName(e.target.value);
									clearInvalid("lastName");
								}}
								autoComplete="family-name"
							/>
						</div>
					</div>

					{/* Email */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.email}</label>
						<input
							className={`sf-auth-input${invalidClass("email")}`}
							type="email"
							placeholder={t.email}
							value={email}
							onChange={(e) => {
								setEmail(e.target.value);
								clearInvalid("email");
							}}
							autoComplete="email"
						/>
					</div>

					{/* Phone */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.phone}</label>
						<PhoneInput
							businessPhone={phone}
							setBusinessPhone={(value) => {
								setPhone(value);
								clearInvalid("phone");
							}}
							className={invalidFields.has("phone") ? "is-invalid" : ""}
						/>
					</div>

					<p className="sf-auth-legend">{t.sectionSecurity}</p>

					{/* Password */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.password}</label>
						<PasswordField
							value={password}
							onChange={(value) => {
								setPassword(value);
								clearInvalid("password");
							}}
							placeholder={t.password}
							autoComplete="new-password"
							className={invalidClass("password").trim()}
						/>
						<PasswordStrength value={password} />
					</div>

					{/* Confirm password */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.confirmPassword}</label>
						<PasswordField
							value={confirmPassword}
							onChange={(value) => {
								setConfirmPassword(value);
								clearInvalid("confirmPassword");
							}}
							placeholder={t.confirmPassword}
							autoComplete="new-password"
							className={invalidClass("confirmPassword").trim()}
						/>
						{confirmPassword && (
							<p className={`sf-auth-match ${password === confirmPassword ? "is-ok" : "is-bad"}`} aria-live="polite">
								{password === confirmPassword ? <Check size={13} strokeWidth={3} /> : <X size={13} strokeWidth={3} />}
								{password === confirmPassword ? t.match.ok : t.match.bad}
							</p>
						)}
					</div>

					{/* Remember me */}
					<div className="sf-auth-check is-quiet">
						<input type="checkbox" id="rememberMe" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
						<label htmlFor="rememberMe">{t.rememberMe}</label>
					</div>

					{/* Terms */}
					<div className={`sf-auth-check${invalidClass("terms")}`}>
						<input
							type="checkbox"
							id="acceptTerms"
							checked={acceptedTerms}
							onChange={(e) => {
								setAcceptedTerms(e.target.checked);
								clearInvalid("terms");
							}}
						/>
						<label htmlFor="acceptTerms">
							{t.acceptTerms}{" "}
							<span className="sf-auth-check-link" onClick={() => setActiveModal("terms")}>
								{t.termsLink}
							</span>
						</label>
					</div>

					{/* Submit */}
					<button className="sf-auth-cta" onClick={handleRegister} disabled={loading}>
						{loading && <span className="sf-auth-spinner" aria-hidden="true" />}
						{loading ? t.loading : t.submit}
					</button>

					{/* Footer */}
					<p className="sf-auth-foot">
						{t.haveAccount} <Link to="/signin">{t.signIn}</Link>
					</p>
				</div>
			</div>

			{banner && <BannerMessageFixed type={banner.type} text={banner.text} exiting={exiting} />}
			{activeModal === "terms" && <TermsOfServiceModal isOpen onClose={() => setActiveModal(null)} />}
		</div>
	);
};

export default RegisterPage;
